import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Mail, Hammer, Sparkles, ShieldCheck, Lock } from 'lucide-react';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

const UnderConstruction = () => {
  const [email, setEmail] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      toast.error('Please enter a valid email address');
      return;
    }
    setSubmitting(true);
    try {
      const res = await fetch(`${API}/api/waitlist`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, source: 'under-construction' }),
      });
      if (!res.ok) throw new Error('Request failed');
      setSubmitted(true);
      toast.success("You're on the list — we'll let you know when we go live!");
    } catch (err) {
      toast.error('Something went wrong. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-blue-950 to-slate-900 flex items-center justify-center px-6 py-16 relative overflow-hidden">
      {/* Decorative backdrop */}
      <div className="absolute inset-0 opacity-20 pointer-events-none">
        <div className="absolute top-1/4 -left-20 w-96 h-96 bg-orange-500 rounded-full blur-[120px]" />
        <div className="absolute bottom-1/4 -right-20 w-96 h-96 bg-blue-500 rounded-full blur-[120px]" />
      </div>

      <div className="relative max-w-3xl w-full text-center">
        {/* Logo / wordmark */}
        <div className="flex items-center justify-center gap-3 mb-10">
          <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-blue-500 to-blue-700 flex items-center justify-center shadow-lg shadow-blue-500/30">
            <Hammer className="w-6 h-6 text-white" />
          </div>
          <span className="text-2xl font-bold text-white tracking-tight" data-testid="uc-wordmark">
            HomeFixer<span className="text-orange-400">360</span>
          </span>
        </div>

        {/* Pill */}
        <div className="inline-flex items-center gap-2 bg-orange-500/15 border border-orange-500/30 text-orange-300 px-4 py-1.5 rounded-full text-sm font-medium mb-8">
          <Sparkles className="w-4 h-4" />
          Under Construction
        </div>

        {/* Headline */}
        <h1
          className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white tracking-tight leading-[1.05] mb-6"
          data-testid="uc-headline"
        >
          We're sprucing things <span className="text-orange-400">up.</span>
        </h1>

        {/* Subline */}
        <p
          className="text-lg sm:text-xl text-slate-300 max-w-2xl mx-auto mb-10 leading-relaxed"
          data-testid="uc-subline"
        >
          HomeFixer360 is getting a major upgrade — a faster, smarter customer
          acquisition platform built for every home-service trade. Drop your
          email and we'll let you know the moment we go live.
        </p>

        {/* Email capture */}
        {!submitted ? (
          <form
            onSubmit={handleSubmit}
            className="max-w-lg mx-auto flex flex-col sm:flex-row gap-3 mb-10"
            data-testid="uc-waitlist-form"
          >
            <div className="relative flex-1">
              <Mail className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
              <input
                type="email"
                placeholder="you@yourcompany.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                disabled={submitting}
                className="w-full pl-12 pr-4 py-3.5 rounded-xl bg-white/10 border border-white/15 text-white placeholder-slate-400 focus:outline-none focus:border-orange-400/70 focus:bg-white/15 transition disabled:opacity-50"
                data-testid="uc-email-input"
                required
              />
            </div>
            <button
              type="submit"
              disabled={submitting}
              className="px-7 py-3.5 rounded-xl bg-orange-500 hover:bg-orange-400 text-white font-semibold transition shadow-lg shadow-orange-500/30 disabled:opacity-50 disabled:cursor-not-allowed"
              data-testid="uc-submit-btn"
            >
              {submitting ? 'Adding…' : 'Notify Me'}
            </button>
          </form>
        ) : (
          <div
            className="max-w-lg mx-auto bg-emerald-500/15 border border-emerald-500/30 text-emerald-200 rounded-xl px-6 py-4 mb-10 flex items-center justify-center gap-2"
            data-testid="uc-success-message"
          >
            <ShieldCheck className="w-5 h-5" />
            You're on the list. Talk soon.
          </div>
        )}

        {/* Contact line */}
        <p className="text-sm text-slate-400">
          Need to reach us in the meantime?{' '}
          <a
            href="mailto:guy@ideaguybrands.com"
            className="text-orange-300 hover:text-orange-200 underline underline-offset-4"
            data-testid="uc-contact-email"
          >
            guy@ideaguybrands.com
          </a>
        </p>
      </div>

      {/* Discreet Admin Login */}
      <Link
        to="/login"
        className="absolute top-6 right-6 inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-white/5 hover:bg-white/10 border border-white/10 hover:border-white/20 text-slate-300 hover:text-white text-sm font-medium transition backdrop-blur-sm z-10"
        data-testid="uc-admin-login-link"
      >
        <Lock className="w-4 h-4" />
        Admin Login
      </Link>
    </div>
  );
};

export default UnderConstruction;
